import Call from "@/components/common/Call";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/cn";
import { Link } from "@tanstack/react-router";
import dayjs from "dayjs";
import { CheckCircle, ClipboardSignature, ExternalLink } from "lucide-react";
import useConsentDialog from "./hooks/useConsentDialog";

interface ConsentCallProps {
	diaryId?: string;
}

const ConsentCall: React.FC<ConsentCallProps> = ({ diaryId }) => {
	// Use the useConsentDialog hook to determine which consents need signing
	const { consents, unsignedConsents } = useConsentDialog({
		diaryId,
		isLoading: false,
	});

	if (consents.length === 0) return null;

	return (
		<div className="space-y-3">
			{consents.map((consent) => {
				// Check if this consent is in the unsignedConsents array
				const isSigned = !unsignedConsents.some(
					(uc) => uc.consent_id === consent.consent_id,
				);

				// Get the signed date from the first signature if available
				const signatures = Array.isArray(consent.signatures)
					? consent.signatures
					: [];
				const signedAt =
					consent.signed_at ||
					(signatures.length > 0 ? signatures[0]?.signed_at : null);

				return (
					<Call
						key={consent.consent_id || ""}
						icon={isSigned ? <CheckCircle /> : <ClipboardSignature />}
						variant={isSigned ? "info" : "warning"}
						words={`${consent.consent_name || "Consent"} Form`}
						size="md"
						className={cn(isSigned ? "pb-2" : "pb-4 pr-4")}
						button={
							isSigned ? (
								<Button variant="link" size="sm" asChild className="px-2 h-7">
									<Link
										to="/preview/consent/$consentId"
										params={{
											consentId: consent.consent_id || "",
										}}
									>
										Details
										<ExternalLink size={16} className={cn("mr-1")} />
									</Link>
								</Button>
							) : (
								<Button
									variant="default"
									size="sm"
									asChild
									className="bg-amber-600 hover:bg-amber-700 text-white"
								>
									<Link
										to="/preview/consent/$consentId"
										params={{
											consentId: consent.consent_id || "",
										}}
									>
										Sign
									</Link>
								</Button>
							)
						}
					>
						{isSigned ? (
							<div className={cn("flex justify-between items-center")}>
								<span className={cn("text-muted-foreground text-xs")}>
									{signedAt
										? `Signed at ${dayjs(signedAt).format("DD MMM YYYY")}`
										: "Signed"}
								</span>
							</div>
						) : (
							<div
								className={cn(
									"flex flex-col md:flex-row justify-between items-center",
								)}
							>
								<span className={cn("text-amber-700 text-sm font-medium")}>
									Requires signature
								</span>
							</div>
						)}
					</Call>
				);
			})}
		</div>
	);
};

export default ConsentCall;
